import Vue from 'vue'
import VueCookie from 'vue-cookie'
import store from '@/store'
import jwtDecode from 'jwt-decode'
import localStorage from 'store'
import throttle from 'lodash/throttle'
import router from '../router'

Vue.use(VueCookie)

const TOKEN_COOKIE = 'token'
const REFRESH_TOKEN_COOKIE = 'refresh_token'
const ENGCOM_COOKIE = 'hubspotutk'

const TOKEN_KEY = 'token'
const SU_TOKEN_KEY = '_token'
const REFRESH_TOKEN_KEY = 'refresh_token'
const LIMITED_TOKEN_KEY = 'limited'
const UTM_SOURCE_KEY = 'utm_source'
const REFERRAL_CODE_KEY = 'referral_code'

let checkedEngcomCookie = false

/* eslint-disable prefer-promise-reject-errors */
const auth = {
  getToken: () => {
    const token = localStorage.get(TOKEN_KEY)
    return token || Vue.cookie.get(TOKEN_COOKIE)
  },
  getRefreshToken: () => {
    const token = localStorage.get(REFRESH_TOKEN_KEY)
    return token || Vue.cookie.get(REFRESH_TOKEN_COOKIE)
  },
  isLoggedIn: () => {
    return store.getters.loggedIn
  },
  checkLogin: throttle((maxRetries, promise) => {
    return new Promise((resolve) => {
      if (store.getters.loggedIn && auth.getToken()) {
        return resolve(true)
      }

      if (auth.getToken()) {
        return auth.checkAccessToken(5, 100, resolve)
      }

      const cookieValue = Vue.cookie.get(ENGCOM_COOKIE)
      // make sure calling engcom API doesn't happpen on loggedIn users
      if (!auth.isLoggedIn() && cookieValue && !checkedEngcomCookie) {
        return auth.checkEngcomCookie(cookieValue, resolve)
      }

      return resolve(false)
    })
  }, 3000),
  checkEngcomCookie: (cookieValue, resolve) => {
    checkedEngcomCookie = true
    store.dispatch('engcomLogin', { hubspotTrackingID: cookieValue, utmSource: auth.getUtmSource() }).then((tokens) => {
      auth.storeTokens(tokens)
      return resolve(true)
    }, (response) => {
      return resolve(false)
    })
  },
  checkAccessToken: (maxRetries, retryDelay, resolve) => {
    store.dispatch('current').then(() => {
      store.dispatch('haveToken', true)
      auth.syncTokens()
      return resolve(true)
    }, (response) => {
      if (response.status === 401 || response.status === 404) {
        auth.logout(true)
        return resolve(false)
      }

      if (maxRetries > 0) {
        setTimeout(() => { auth.checkAccessToken(maxRetries - 1, retryDelay * 2, resolve) }, retryDelay)
      } else {
        return resolve(false)
      }
    })
  },
  checkRefreshToken: () => {
    return new Promise((resolve, reject) => {
      if (!auth.getToken() || !auth.getRefreshToken()) {
        return reject()
      }

      const username = auth.getUsername()
      if (!username) {
        return reject()
      }

      store.dispatch('refresh', { username: username, token: auth.getRefreshToken() }).then((tokens) => {
        auth.storeTokens(tokens)
        return resolve()
      }, () => {
        return reject()
      })
    })
  },
  logout: (auto) => {
    return new Promise((resolve, reject) => {
      if (auto && localStorage.get(LIMITED_TOKEN_KEY)) {
        return resolve()
      }
      return store.dispatch('logout').then(() => {
        localStorage.clearAll()
        Vue.cookie.delete(TOKEN_COOKIE, { domain: process.env.COOKIE_DOMAIN })
        Vue.cookie.delete(REFRESH_TOKEN_COOKIE, { domain: process.env.COOKIE_DOMAIN })
        // redirect to / on logout for all partners
        router.push('/')
      })
    })
  },
  storeTokens: (tokens) => {
    localStorage.set(TOKEN_KEY, tokens.accessToken)
    localStorage.set(REFRESH_TOKEN_KEY, tokens.refreshToken)

    Vue.cookie.set(TOKEN_COOKIE, tokens.accessToken, { expires: '1M', domain: process.env.COOKIE_DOMAIN, samesite: 'strict' })
    Vue.cookie.set(REFRESH_TOKEN_COOKIE, tokens.refreshToken, { expires: '1Y', domain: process.env.COOKIE_DOMAIN, samesite: 'strict' })

    if (tokens.limited) {
      localStorage.set(LIMITED_TOKEN_KEY, true)
    } else {
      localStorage.remove(LIMITED_TOKEN_KEY)
    }
    store.dispatch('haveToken', true)
  },
  syncTokens: () => {
    let token = localStorage.get(TOKEN_KEY)
    if (token) {
      Vue.cookie.set(TOKEN_COOKIE, token, { expires: '1M', domain: process.env.COOKIE_DOMAIN, samesite: 'strict' })
    } else {
      localStorage.set(TOKEN_KEY, Vue.cookie.get(TOKEN_COOKIE))
    }

    token = localStorage.get(REFRESH_TOKEN_KEY)
    if (token) {
      Vue.cookie.set(REFRESH_TOKEN_COOKIE, token, { expires: '1Y', domain: process.env.COOKIE_DOMAIN, samesite: 'strict' })
    } else {
      localStorage.set(REFRESH_TOKEN_KEY, Vue.cookie.get(REFRESH_TOKEN_COOKIE))
    }
  },
  isAdmin: () => {
    if (!store.getters.loggedIn) {
      return false
    }

    const token = auth.getToken()
    if (token) {
      const contents = jwtDecode(token)
      return contents.role === 'admin'
    }
  },
  isSu: () => {
    return localStorage.get(SU_TOKEN_KEY) !== undefined
  },
  su: (token) => {
    if (auth.isAdmin()) {
      localStorage.set(SU_TOKEN_KEY, auth.getToken())
      localStorage.set(TOKEN_KEY, token)
    }
  },
  exitSu: () => {
    localStorage.set(TOKEN_KEY, localStorage.get(SU_TOKEN_KEY))
    localStorage.remove(SU_TOKEN_KEY)
  },
  isRole: (allowedRoles) => {
    if (!store.getters.loggedIn) {
      return false
    }

    const token = auth.getToken()
    if (token) {
      const contents = jwtDecode(token)
      let found = false
      allowedRoles.forEach((role) => {
        if (contents.role === role) {
          found = true
        }
      })
      return found
    }

    return false
  },
  isAnalytic: () => {
    return auth.isRole(['analytic', 'analytic-curator'])
  },
  isCurator: () => {
    return auth.isRole(['curator', 'analytic-curator'])
  },
  isResourceEditor: () => {
    return auth.isRole(['resourceEditor'])
  },
  isJudgeAdmin: () => {
    return auth.isRole(['judgingAdmin'])
  },
  wasAdmin: () => {
    const token = localStorage.get(SU_TOKEN_KEY)
    return !!token
  },
  getUsername: () => {
    const token = auth.getToken()
    if (token) {
      const contents = jwtDecode(auth.getToken())
      return contents.name
    }
  },
  getUtmSource: () => {
    return localStorage.get(UTM_SOURCE_KEY)
  },
  setUtmSource: (value) => {
    return localStorage.set(UTM_SOURCE_KEY, value)
  },
  clearUTMSource: () => {
    localStorage.remove(UTM_SOURCE_KEY)
  },
  getSessionToken: () => {
    return store.getters.socketUUID
  },
  getReferralCode: () => {
    return localStorage.get(REFERRAL_CODE_KEY)
  },
  setReferralCode: (value) => {
    return localStorage.set(REFERRAL_CODE_KEY, value)
  },
  clearReferralCode: () => {
    localStorage.remove(REFERRAL_CODE_KEY)
  },
  isFeedbackSurveySeen: () => {
    return Vue.cookie.get('feedback')
  },
  setFeedbackSurveySeen: (value) => {
    Vue.cookie.set('feedback', value, { samesite: 'strict' })
  }
}

export default auth
